import React from "react";
import { useNavigate } from "react-router-dom";

export default function Signs() {
  const navigate = useNavigate();

  return (
    <div className="font-sans">

      {/* ================= HERO ================= */}
      <section className="bg-gradient-to-br from-pink-50 via-white to-purple-50 py-24 px-6 text-center">
        <h1 className="text-4xl md:text-5xl font-extrabold text-pink-700 leading-tight">
          Signs of Hormonal Imbalance <br className="hidden sm:block" />
          Every Woman Should Know
        </h1>

        <p className="mt-6 max-w-3xl mx-auto text-gray-700 text-lg md:text-xl">
          Hormonal imbalance can quietly affect energy, mood, sleep, and metabolism.
          Learning to recognize the signs early allows women to take supportive steps
          toward better balance and overall wellbeing.
        </p>

      
      </section>

      {/* ================= CONTENT ================= */}
      <section className="max-w-4xl mx-auto px-6 py-16 space-y-16 text-gray-700">

        {/* WHAT IS IMBALANCE */}
        <div className="flex flex-col md:flex-row items-start gap-6">
          <img
            src="/assets/Glow Picks (3).png"
            alt="Hormonal Imbalance"
            className="w-full md:w-1/4 rounded-xl shadow-lg"
          />
          <div>
            <h2 className="text-3xl font-bold text-pink-600 mb-4">
              What Is Hormonal Imbalance?
            </h2>
            <p className="text-lg leading-relaxed">
              Hormonal imbalance occurs when the body produces too much or too little of one
              or more hormones. Since hormones act as messengers that regulate metabolism,
              energy, mood, sleep, and reproductive health, even small disruptions can affect
              how the body feels and functions.
              <br /><br />
              Many women live with hormonal imbalance without realizing it, mistaking symptoms
              like fatigue, irritability, or sleep problems as “normal stress.” Over time,
              unmanaged imbalances may begin to impact daily quality of life.
            </p>
          </div>
        </div>

        {/* SIGNS */}
        <div className="flex flex-col md:flex-row-reverse items-start gap-6">
          <img
            src="/assets/2.png"
            alt="Signs of Hormonal Imbalance"
            className="w-full md:w-1/2 rounded-xl shadow-lg"
          />
          <div>
            <h2 className="text-3xl font-bold text-pink-600 mb-4">
              Common Signs of Hormonal Imbalance
            </h2>
            <p className="text-lg mb-4">
              Hormonal imbalance can appear differently for every woman, but common warning
              signs include:
            </p>
            <ul className="list-disc list-inside text-lg space-y-2">
              <li><strong className="text-pink-500">Persistent Fatigue:</strong> Feeling tired despite rest</li>
              <li><strong className="text-pink-500">Mood Changes:</strong> Anxiety, irritability, or emotional swings</li>
              <li><strong className="text-pink-500">Skin & Hair Issues:</strong> Acne, dull skin, hair thinning</li>
              <li><strong className="text-pink-500">Weight Changes:</strong> Unexplained gain or difficulty losing weight</li>
              <li><strong className="text-pink-500">Irregular Periods:</strong> Missed, heavy, or painful cycles</li>
              <li><strong className="text-pink-500">Sleep Problems:</strong> Trouble falling or staying asleep</li>
            </ul>
          </div>
        </div>

        {/* WHY IT HAPPENS */}
        <div>
          <h2 className="text-3xl font-bold text-pink-600 mb-4">
            Why Hormonal Imbalance Happens
          </h2>
          <p className="text-lg mb-4">
            Hormonal imbalance often develops due to a combination of modern lifestyle
            factors and internal body stressors:
          </p>
          <ul className="list-disc list-inside text-lg space-y-2">
            <li>Chronic stress and elevated cortisol levels</li>
            <li>Poor or inconsistent sleep patterns</li>
            <li>Nutrient deficiencies</li>
            <li>Lack of regular movement</li>
            <li>Conditions such as PCOS or thyroid imbalance</li>
          </ul>
        </div>

        {/* SUPPORT */}
        <div>
          <h2 className="text-3xl font-bold text-pink-600 mb-4">
            Supporting Hormonal Health Naturally
          </h2>
          <p className="text-lg mb-4">
            While hormonal changes are natural, consistent habits can help support balance
            and reduce uncomfortable symptoms over time.
          </p>
          <ul className="list-disc list-inside text-lg space-y-2">
            <li>Prioritize deep, restorative sleep</li>
            <li>Eat balanced meals rich in nutrients</li>
            <li>Practice stress management techniques</li>
            <li>Stay hydrated throughout the day</li>
            <li>Use supportive supplements when needed</li>
          </ul>
        </div>

        {/* ================= SUPPLEMENT FEATURE ================= */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-pink-100">
          <h3 className="text-2xl font-bold text-pink-700 mb-4">
            Featured Supplement: Magnesium
          </h3>

          <div className="flex flex-col md:flex-row gap-6 items-center">
            <img
              src="/assets/MG.jpeg"
              alt="Magnesium Supplement"
              className="w-full md:w-1/3 rounded-xl shadow"
            />

            <div>
              <p className="text-lg mb-4">
                Magnesium plays a vital role in supporting the nervous system, muscle function,
                stress response, and sleep quality. Low magnesium levels are commonly linked to
                fatigue, anxiety, muscle tension, and poor sleep — all frequent signs of hormonal
                imbalance.
                <br /><br />
                Supporting magnesium intake may help promote relaxation, improve sleep quality,
                and support the body’s natural stress response, making it a helpful addition to
                a hormone-supportive routine.
              </p>

              <a
                href="https://amzn.to/45kvqZu"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 bg-pink-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-pink-600 transition"
              >
                Shop Magnesium on Amazon →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ================= DISCLAIMER ================= */}
      <section className="bg-gray-50 py-10 px-6 text-center">
        <p className="max-w-3xl mx-auto text-sm text-gray-600">
          This content is for educational purposes only and does not replace medical advice.
          Always consult a healthcare professional before starting supplements.
          As an Amazon Associate, Glow Picks may earn from qualifying purchases.
        </p>
      </section>

    </div>
  );
}
